import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box, Typography, Avatar, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Alert, Tabs, Tab,
  Tooltip, Badge,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  CloudUpload as UploadIcon,
  PhotoCamera as PhotoCameraIcon,
  Close as CloseIcon,
  FlipCameraIos as FlipIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Replay as RetakeIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

interface PatientAvatarUploadProps {
  patientId: string;
  currentAvatarUrl?: string;
  patientName: string;
  size?: number;
  onAvatarUpdated?: (newAvatarUrl: string) => void;
  editable?: boolean;
}

const PatientAvatarUpload: React.FC<PatientAvatarUploadProps> = ({
  patientId,
  currentAvatarUrl,
  patientName,
  size = 80,
  onAvatarUpdated,
  editable = true,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState(currentAvatarUrl || '');

  // Webcam states
  const [webcamActive, setWebcamActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setAvatarUrl(currentAvatarUrl || '');
  }, [currentAvatarUrl]);

  // Start webcam
  const startWebcam = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 640 }, height: { ideal: 640 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setWebcamActive(true);
    } catch (err: any) {
      console.error('Error starting webcam:', err);
      setError('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
    }
  }, [facingMode]);

  // Stop webcam
  const stopWebcam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setWebcamActive(false);
  }, []);

  // Capture photo from webcam
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Square crop from center
    const minDim = Math.min(video.videoWidth, video.videoHeight);
    const sx = (video.videoWidth - minDim) / 2;
    const sy = (video.videoHeight - minDim) / 2;

    canvas.width = 400;
    canvas.height = 400;

    // Mirror for front camera
    if (facingMode === 'user') {
      ctx.translate(400, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, sx, sy, minDim, minDim, 0, 0, 400, 400);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(dataUrl);
    stopWebcam();
  }, [facingMode, stopWebcam]);

  // Flip camera
  const flipCamera = useCallback(() => {
    stopWebcam();
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  }, [stopWebcam]);

  // Effect to restart webcam when facing mode changes
  useEffect(() => {
    if (dialogOpen && activeTab === 1 && !capturedImage) {
      startWebcam();
    }
    return () => {
      stopWebcam();
    };
  }, [facingMode]);

  // Upload dataUrl as file
  const uploadDataUrl = useCallback(async (dataUrl: string) => {
    setUploading(true);
    setError(null);
    try {
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], `avatar-${patientId}-${Date.now()}.jpg`, { type: 'image/jpeg' });

      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post(
        `${getApiUrl()}/clinic/patients/${patientId}/avatar`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (data.success) {
        setAvatarUrl(data.data.avatar_url);
        onAvatarUpdated?.(data.data.avatar_url);
        setDialogOpen(false);
        setCapturedImage(null);
      } else {
        setError(data.error || 'Erro ao fazer upload');
      }
    } catch (err: any) {
      console.error('Error uploading avatar:', err);
      setError(err.response?.data?.error || 'Erro ao fazer upload da foto');
    } finally {
      setUploading(false);
    }
  }, [patientId, onAvatarUpdated]);

  // Handle file upload
  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Selecione apenas arquivos de imagem');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('A imagem deve ter no máximo 10MB');
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post(
        `${getApiUrl()}/clinic/patients/${patientId}/avatar`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (data.success) {
        setAvatarUrl(data.data.avatar_url);
        onAvatarUpdated?.(data.data.avatar_url);
        setDialogOpen(false);
      } else {
        setError(data.error || 'Erro ao fazer upload');
      }
    } catch (err: any) {
      console.error('Error uploading avatar:', err);
      setError(err.response?.data?.error || 'Erro ao fazer upload da foto');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [patientId, onAvatarUpdated]);

  // Handle dialog close
  const handleClose = () => {
    stopWebcam();
    setCapturedImage(null);
    setError(null);
    setDialogOpen(false);
  };

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setCapturedImage(null);
    setError(null);
    if (newValue === 1) {
      setTimeout(() => startWebcam(), 300);
    } else {
      stopWebcam();
    }
  };

  const initials = patientName
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <>
      {/* Avatar with edit overlay */}
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            editable ? (
              <Tooltip title="Alterar foto">
                <IconButton
                  size="small"
                  onClick={() => setDialogOpen(true)}
                  sx={{
                    bgcolor: '#14B8A6',
                    color: 'white',
                    width: size * 0.35,
                    height: size * 0.35,
                    minWidth: 28,
                    minHeight: 28,
                    border: '2px solid white',
                    '&:hover': { bgcolor: '#0d9488' },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  <CameraIcon sx={{ fontSize: Math.max(14, size * 0.18) }} />
                </IconButton>
              </Tooltip>
            ) : null
          }
        >
          <Avatar
            src={avatarUrl || undefined}
            sx={{
              width: size,
              height: size,
              bgcolor: avatarUrl ? 'transparent' : '#14B8A6',
              fontSize: size * 0.35,
              fontWeight: 700,
              cursor: editable ? 'pointer' : 'default',
              border: '3px solid rgba(20,184,166,0.2)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              '&:hover': editable ? {
                opacity: 0.85,
                boxShadow: '0 4px 20px rgba(20,184,166,0.3)',
              } : {},
            }}
            onClick={editable ? () => setDialogOpen(true) : undefined}
          >
            {!avatarUrl && (initials || <PersonIcon sx={{ fontSize: size * 0.5 }} />)}
          </Avatar>
        </Badge>
      </Box>

      {/* Upload Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          bgcolor: '#f0fdfa', borderBottom: '1px solid rgba(20,184,166,0.12)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PhotoCameraIcon sx={{ color: '#14B8A6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Foto do Paciente
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {error && (
            <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            sx={{
              borderBottom: '1px solid rgba(20,184,166,0.1)',
              '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' },
              '& .Mui-selected': { color: '#14B8A6' },
              '& .MuiTabs-indicator': { bgcolor: '#14B8A6' },
            }}
          >
            <Tab icon={<UploadIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Upload do PC" />
            <Tab icon={<CameraIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Câmera / Webcam" />
          </Tabs>

          {/* Tab 0: File Upload */}
          {activeTab === 0 && (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              {/* Preview */}
              <Avatar
                src={avatarUrl || undefined}
                sx={{
                  width: 160, height: 160,
                  bgcolor: avatarUrl ? 'transparent' : '#e2e8f0',
                  fontSize: 48, fontWeight: 700, color: '#94a3b8',
                  border: '4px dashed rgba(20,184,166,0.3)',
                }}
              >
                {!avatarUrl && (initials || <PersonIcon sx={{ fontSize: 64 }} />)}
              </Avatar>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155', mb: 0.5 }}>
                  {patientName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecione uma imagem do computador
                </Typography>
              </Box>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />

              <Button
                variant="contained"
                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                sx={{
                  bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' },
                  textTransform: 'none', fontWeight: 600, borderRadius: 2,
                  px: 4, py: 1.2,
                }}
              >
                {uploading ? 'Enviando...' : 'Selecionar Imagem'}
              </Button>

              <Typography variant="caption" color="text.secondary">
                Formatos aceitos: JPG, PNG, WebP (máx. 10MB)
              </Typography>
            </Box>
          )}

          {/* Tab 1: Webcam Capture */}
          {activeTab === 1 && (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {!capturedImage ? (
                <>
                  {/* Video preview */}
                  <Box sx={{
                    width: 320, height: 320,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '4px solid rgba(20,184,166,0.3)',
                    bgcolor: '#0f172a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative',
                  }}>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                      }}
                    />
                    {!webcamActive && (
                      <Box sx={{
                        position: 'absolute', inset: 0,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: 1,
                      }}>
                        <CameraIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.5)' }} />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          Iniciando câmera...
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Camera controls */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Tooltip title="Trocar câmera">
                      <IconButton onClick={flipCamera} sx={{ color: '#64748b' }}>
                        <FlipIcon />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      onClick={capturePhoto}
                      disabled={!webcamActive}
                      sx={{
                        bgcolor: '#14B8A6', color: 'white',
                        width: 64, height: 64,
                        '&:hover': { bgcolor: '#0d9488' },
                        '&:disabled': { bgcolor: '#cbd5e1', color: 'white' },
                        boxShadow: '0 4px 14px rgba(20,184,166,0.4)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <PhotoCameraIcon sx={{ fontSize: 32 }} />
                    </IconButton>
                    <Box sx={{ width: 40 }} /> {/* Spacer */}
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Posicione o rosto no centro e clique para capturar
                  </Typography>
                </>
              ) : (
                <>
                  {/* Captured image preview */}
                  <Box sx={{
                    width: 320, height: 320,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '4px solid #14B8A6',
                    boxShadow: '0 4px 20px rgba(20,184,166,0.3)',
                  }}>
                    <img
                      src={capturedImage}
                      alt="Captured"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<RetakeIcon />}
                      onClick={() => {
                        setCapturedImage(null);
                        setTimeout(() => startWebcam(), 300);
                      }}
                      sx={{
                        borderColor: '#64748b', color: '#64748b',
                        textTransform: 'none', fontWeight: 600, borderRadius: 2,
                        '&:hover': { borderColor: '#475569', bgcolor: 'rgba(100,116,139,0.04)' },
                      }}
                    >
                      Nova Foto
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
                      onClick={() => uploadDataUrl(capturedImage)}
                      disabled={uploading}
                      sx={{
                        bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' },
                        textTransform: 'none', fontWeight: 600, borderRadius: 2,
                        px: 3,
                      }}
                    >
                      {uploading ? 'Salvando...' : 'Usar Esta Foto'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Hidden canvas for capture */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(20,184,166,0.08)' }}>
          <Button onClick={handleClose} sx={{ color: '#64748b', textTransform: 'none' }}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PatientAvatarUpload;
